/**
 * Feedback Service
 * Microservice for handling user feedback submissions via triple-click feature
 */

import { supabase } from './supabaseClient';

/**
 * Get the current authenticated user
 */
const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('❌ [FeedbackService] Error getting user:', error);
    throw error;
  }
  return user;
};

/**
 * Check if user has exceeded rate limit (5 submissions per day)
 * @param {string} userId - User ID
 * @returns {Promise<{allowed: boolean, remaining: number}>}
 */
export const checkRateLimit = async (userId) => {
  try {

    // Get start of today in user's timezone
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Count feedback submitted today
    const { data, error } = await supabase
      .from('user_feedback')
      .select('id')
      .eq('user_id', userId)
      .gte('created_at', today.toISOString());

    if (error) {
      console.error('❌ [FeedbackService] Error checking rate limit:', error);
      throw error;
    }

    const submittedToday = data?.length || 0;
    const maxDaily = 5;
    const remaining = Math.max(0, maxDaily - submittedToday);
    const allowed = submittedToday < maxDaily;


    return { allowed, remaining, submitted: submittedToday, max: maxDaily };
  } catch (error) {
    console.error('❌ [FeedbackService] Rate limit check failed:', error);
    throw error;
  }
};

/**
 * Get remaining submissions for user today
 * @param {string} userId - User ID
 * @returns {Promise<number>}
 */
export const getRemainingSubmissions = async (userId) => {
  const { remaining } = await checkRateLimit(userId);
  return remaining;
};

/**
 * Generate a unique CSS selector for an element
 * @param {HTMLElement} element - The element to generate selector for
 * @returns {string} CSS selector
 */
export const getElementSelector = (element) => {
  try {
    if (!element) return 'unknown';

    // If element has an ID, use that
    if (element.id) {
      return `#${element.id}`;
    }

    // Build path from element to root
    const path = [];
    let current = element;

    while (current && current.nodeType === Node.ELEMENT_NODE) {
      let selector = current.nodeName.toLowerCase();

      // Add classes if available
      if (current.className && typeof current.className === 'string') {
        const classes = current.className.trim().split(/\s+/).filter(c => c);
        if (classes.length > 0) {
          selector += '.' + classes.slice(0, 3).join('.'); // Max 3 classes
        }
      }

      // Add nth-child if needed for specificity
      if (current.parentNode) {
        const siblings = Array.from(current.parentNode.children);
        const index = siblings.indexOf(current);
        if (siblings.length > 1) {
          selector += `:nth-child(${index + 1})`;
        }
      }

      path.unshift(selector);
      current = current.parentNode;

      // Limit path depth to 5 levels
      if (path.length >= 5) break;
    }

    return path.join(' > ');
  } catch (error) {
    console.error('❌ [FeedbackService] Error generating selector:', error);
    return 'unknown';
  }
};

/**
 * Get browser and device information
 * @returns {object} Browser info
 */
export const getBrowserInfo = () => {
  try {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ [FeedbackService] Error getting browser info:', error);
    return {};
  }
};

/**
 * Submit feedback
 * @param {object} feedbackData - Feedback data
 * @param {string} feedbackData.issueType - Type: bug, feature_request, ui_issue, other
 * @param {string} feedbackData.description - Description of the issue
 * @param {string} feedbackData.expectedBehavior - Expected behavior (optional)
 * @param {string} feedbackData.pageUrl - Current page URL
 * @param {string} feedbackData.elementPath - CSS selector of element
 * @param {string} feedbackData.elementSnapshot - outerHTML of the element (optional)
 * @param {object} feedbackData.browserInfo - Browser/device info
 * @returns {Promise<object>} Created feedback record
 */
export const submitFeedback = async (feedbackData) => {
  try {

    // Get current user
    const user = await getCurrentUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check rate limit
    const { allowed, remaining } = await checkRateLimit(user.id);
    if (!allowed) {
      throw new Error(`Rate limit exceeded. You can submit ${remaining} more feedback today. Limit resets at midnight.`);
    }

    // Prepare feedback record
    const feedbackRecord = {
      user_id: user.id,
      user_email: user.email,
      issue_type: feedbackData.issueType,
      title: feedbackData.title,
      description: feedbackData.description,
      expected_behavior: feedbackData.expectedBehavior || null,
      page_url: feedbackData.pageUrl,
      element_path: feedbackData.elementPath,
      element_snapshot: feedbackData.elementSnapshot || null,
      browser_info: feedbackData.browserInfo || getBrowserInfo(),
      status: 'open',
      vote_count: 0,
      comment_count: 0
    };

    // Insert into database
    const { data, error } = await supabase
      .from('user_feedback')
      .insert(feedbackRecord)
      .select()
      .single();

    if (error) {
      console.error('❌ [FeedbackService] Error submitting feedback:', error);
      throw error;
    }


    return {
      success: true,
      feedback: data,
      remaining: remaining - 1
    };
  } catch (error) {
    console.error('❌ [FeedbackService] Submit feedback failed:', error);
    throw error;
  }
};

/**
 * Get user's feedback history
 * @param {string} userId - User ID
 * @param {number} limit - Max number of records to return
 * @returns {Promise<Array>} Array of feedback records
 */
export const getUserFeedbackHistory = async (userId, limit = 10) => {
  try {

    const { data, error } = await supabase
      .from('user_feedback')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('❌ [FeedbackService] Error getting feedback history:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('❌ [FeedbackService] Get feedback history failed:', error);
    throw error;
  }
};

/**
 * Delete user's feedback
 * @param {string} feedbackId - Feedback ID
 * @returns {Promise<boolean>}
 */
export const deleteFeedback = async (feedbackId) => {
  try {

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('user_feedback')
      .delete()
      .eq('id', feedbackId)
      .eq('user_id', user.id);

    if (error) {
      console.error('❌ [FeedbackService] Error deleting feedback:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('❌ [FeedbackService] Delete feedback failed:', error);
    throw error;
  }
};
